import { useEffect, useMemo, useState } from 'react';
import { useOrthodontistUsers } from '../../../../hooks/useOrthodontistUsers';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import type { OrthodontistUserRow } from '../../../../services/orthodontistAccounts';
import { AddOrthodontistUserModal } from './AddOrthodontistUserModal';
import { RemoveOrthodontistUserModal } from './RemoveOrthodontistUserModal';
import { OrthodontistUsersTable } from './OrthodontistUsersTable';

interface OrthodontistUsersTabProps {
    orthodontistId: string;
    orthodontistName: string;
}

export function OrthodontistUsersTab({
    orthodontistId,
    orthodontistName
}: OrthodontistUsersTabProps) {
    const { showToast } = useToast();
    const { users, isLoading, isError, error } = useOrthodontistUsers(orthodontistId);
    const [search, setSearch] = useState('');
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<OrthodontistUserRow | null>(null);

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load users'), 'error');
        }
    }, [isError, error, showToast]);

    // Client-side filter: the backend returns every org member in one
    // unpaginated array (see useOrthodontistUsers) - no server search to call.
    const trimmedSearch = search.trim().toLowerCase();
    const filteredUsers = useMemo(() => {
        if (!trimmedSearch) return users;
        return users.filter(
            (user) =>
                user.name.toLowerCase().includes(trimmedSearch) ||
                user.email.toLowerCase().includes(trimmedSearch)
        );
    }, [users, trimmedSearch]);

    const existingUserIds = useMemo(() => new Set(users.map((user) => user.userId)), [users]);

    return (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
            <OrthodontistUsersTable
                users={filteredUsers}
                isLoading={isLoading}
                isSearching={Boolean(trimmedSearch)}
                search={{ value: search, onChange: setSearch, placeholder: 'Search org users' }}
                onAddUser={() => setIsAddOpen(true)}
                onRemove={setRemoveTarget}
            />
            {isAddOpen && (
                <AddOrthodontistUserModal
                    orthodontistId={orthodontistId}
                    orthodontistName={orthodontistName}
                    existingUserIds={existingUserIds}
                    onClose={() => setIsAddOpen(false)}
                />
            )}
            {removeTarget && (
                <RemoveOrthodontistUserModal
                    orthodontistId={orthodontistId}
                    orthodontistName={orthodontistName}
                    user={removeTarget}
                    onClose={() => setRemoveTarget(null)}
                />
            )}
        </div>
    );
}
